// src/shoppingFlow/cartPage.js
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus, User, LogOut } from 'lucide-react';
import API_BASE from '../apiBase';
import QaAlert from '../components/QaAlert';


import placeholderImg from '../img/thumbnail_product.png';
const PLACEHOLDER_IMG = placeholderImg;

function fmt(amount, currency = 'CAD') {
  const n = Number(amount ?? 0);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

export default function CartPage() {
  // auth (same as HomePage)
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [cartCount, setCartCount] = useState(0);

  const [items, setItems] = useState([]);
  const [currency, setCurrency] = useState('CAD');
  const [subtotal, setSubtotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // error surface (always-mounted alert)
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const me = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
        if (alive) setUser(me.ok ? await me.json() : null);
      } catch {
        // auth failure is not fatal here
      }
      try {
        const c = await fetch(`${API_BASE}/api/cart/count`, { credentials: 'include' });
        const j = await c.json();
        if (alive) setCartCount(Number(j.count || 0));
      } catch {
        // ignore
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    function onDocClick(e) { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); }
    function onKey(e) { if (e.key === 'Escape') setMenuOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  async function handleLogout() {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {}
    window.location.href = '/';
  }

  async function loadCart() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/cart`, { credentials: 'include' });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to load cart.');
      }
      const data = await res.json();
      setItems(data.items || []);
      setCurrency(data.totals?.currency || 'CAD');
      setSubtotal(Number(data.totals?.subtotal || 0));
      setCartCount(Number(data.totals?.count || 0));
    } catch (e) {
      setItems([]);
      setSubtotal(0);
      setCartCount(0);
      setError(e.message || 'Failed to load cart.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadCart(); }, []);

  async function changeQty(id, nextQty) {
    setError('');
    try {
      const q = Math.max(0, nextQty);
      const res = await fetch(`${API_BASE}/api/cart/items/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ quantity: q }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to update item.');
      }
      await loadCart();
    } catch (e) {
      setError(e.message || 'Failed to update item.');
    }
  }

  async function removeItem(id) {
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/cart/items/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to remove item.');
      }
      await loadCart();
    } catch (e) {
      setError(e.message || 'Failed to remove item.');
    }
  }

  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50" data-qa="cart-page">
      {/* Header (compact) */}
      <header className="bg-white shadow sticky top-0 z-50" data-qa="cart-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent"
            data-qa="cart-logo"
          >
            SodaSips
          </Link>
          <div className="flex items-center gap-4">
            {!user ? (
              <Link to="/signin" className="text-gray-700 hover:text-orange-500" data-qa="cart-signin-link">
                Sign In
              </Link>
            ) : (
              <div className="relative" ref={menuRef} data-qa="cart-user-menu-root">
                <button
                  onClick={() => setMenuOpen(s => !s)}
                  className="text-gray-700 hover:text-orange-500 flex items-center gap-2"
                  data-qa="cart-user-menu-button"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                >
                  <User className="w-5 h-5" />
                  <span className="hidden sm:inline">Hello, {user.first_name}</span>
                </button>
                {menuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden"
                    data-qa="cart-user-menu"
                  >
                    <Link
                      role="menuitem"
                      to="/userprofile"
                      onClick={() => setMenuOpen(false)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50"
                      data-qa="cart-user-menu-profile"
                    >
                      <User className="w-4 h-4" />
                      <span>My Profile</span>
                    </Link>

                    <div className="h-px bg-gray-100 my-1" />

                    <button
                      role="menuitem"
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-gray-700 hover:bg-gray-50"
                      data-qa="cart-user-menu-logout"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            )}
            <Link to="/cart" className="relative p-2 text-gray-700 hover:text-orange-500" data-qa="cart-link">
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-orange-500 text-white rounded-full min-w-5 h-5 px-1 text-[11px] flex items-center justify-center"
                  data-qa="cart-badge"
                >
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-qa="cart-main">
        <h1 className="text-3xl font-bold text-gray-800 mb-4" data-qa="cart-title">Your Cart</h1>

        {/* Always-mounted page-level alert (for network & server errors) */}
        <QaAlert id="cart-alert" dataQa="qa-cart-alert" message={error} variant="error" />

        {loading ? (
          <div className="text-gray-500" data-qa="cart-loading">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-8 text-center" data-qa="cart-empty">
            <p className="text-gray-600 mb-4" data-qa="cart-empty-text">Your cart is empty.</p>
            <Link
              to="/"
              className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg"
              data-qa="cart-empty-continue"
            >
              Continue Shopping
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-qa="cart-grid">
            {/* Items */}
            <div className="lg:col-span-2 space-y-4" data-qa="cart-items">
              {items.map(it => (
                <div
                  key={it.id}
                  className="bg-white rounded-xl shadow p-4 flex gap-4"
                  data-qa="cart-item"
                  data-item-id={it.id}
                  data-product-id={it.product_id}
                >
                  <img
                    src={it.image_url || PLACEHOLDER_IMG}
                    alt={it.product_name}
                    onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                    className="w-28 h-28 object-cover rounded"
                    data-qa="cart-item-image"
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-800" data-qa="cart-item-name">{it.product_name}</h3>
                        <p className="text-sm text-gray-500" data-qa="cart-item-pack">{it.pack_size || '—'}</p>
                      </div>
                      <button
                        onClick={() => removeItem(it.id)}
                        className="text-gray-400 hover:text-red-600"
                        title="Remove"
                        data-qa="cart-item-remove"
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2" data-qa="cart-item-qty">
                        <button
                          onClick={() => changeQty(it.id, it.quantity - 1)}
                          className="w-8 h-8 rounded border hover:bg-gray-50 flex items-center justify-center"
                          aria-label="Decrease quantity"
                          data-qa="cart-item-qty-dec"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="px-3" data-qa="cart-item-qty-value">{it.quantity}</span>
                        <button
                          onClick={() => changeQty(it.id, it.quantity + 1)}
                          className="w-8 h-8 rounded border hover:bg-gray-50 flex items-center justify-center"
                          aria-label="Increase quantity"
                          data-qa="cart-item-qty-inc"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="font-semibold text-gray-800" data-qa="cart-item-line-total">
                        {fmt(Number(it.price) * Number(it.quantity), it.currency)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="bg-white rounded-xl shadow p-6 h-fit" data-qa="cart-summary">
              <h2 className="text-lg font-semibold text-gray-800 mb-4" data-qa="cart-summary-title">Order Summary</h2>
              <div className="flex items-center justify-between mb-2" data-qa="cart-summary-subtotal-row">
                <span className="text-gray-600" data-qa="cart-summary-subtotal-label">Subtotal</span>
                <span className="font-semibold" data-qa="cart-summary-subtotal-value">{fmt(subtotal, currency)}</span>
              </div>
              <p className="text-xs text-gray-500 mb-4" data-qa="cart-summary-note">
                Taxes and shipping calculated at checkout.
              </p>
              <button
                onClick={() => navigate('/checkout')}
                className="w-full bg-gray-900 hover:bg-black text-white font-semibold py-3 rounded-lg"
                data-qa="cart-summary-checkout"
              >
                Buy These Items
              </button>
              <Link
                to="/"
                className="block mt-3 text-center text-sm text-orange-600 hover:underline"
                data-qa="cart-summary-continue"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
